import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import {
  canAccess,
  getActorFromSale,
  isQuoterPlanResource,
} from "../commons/canAccess";
import {
  getProjectScope,
  isCarouselResource,
} from "../commons/canAccessCarousel";
import { clearAdvisorCodePromptDismissal } from "../../sales/advisorCodePromptDismissal";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      // `role`/`business_arm`/`advisor_code` ride along on `identity`
      // (ra-core's `UserIdentity` allows extra keys) so the UI can read the
      // current user's lead_manager scope, and whether her clave is set,
      // without an extra request (TASK-063, TASK-067).
      const actor = getActorFromSale(sale);
      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
        role: actor.role,
        business_arm: actor.business_arm,
        advisor_code: sale.advisor_code ?? null,
        // Almikia Epic EL3: the per-person permission, for `canReassignProtectedLeads(identity)`.
        can_reassign_protected_leads:
          sale.can_reassign_protected_leads === true,
        // Almikia Epic EW3: the per-person permission for the 'Sin clasificar' tray.
        can_classify_whatsapp_contacts:
          sale.can_classify_whatsapp_contacts === true,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    const cachedSale = JSON.parse(cachedValue);
    // Sales cached before roles/advisor_code existed must be fetched again
    if (
      cachedSale.role != null &&
      "advisor_code" in cachedSale &&
      "can_reassign_protected_leads" in cachedSale &&
      "can_classify_whatsapp_contacts" in cachedSale
    ) {
      return cachedSale;
    }
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select(
      "id, first_name, last_name, avatar, administrator, role, business_arm, advisor_code, can_reassign_protected_leads, can_classify_whatsapp_contacts",
    )
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
  // Almikia Epic E3b (TASK-067): a postponed advisor-code prompt is
  // session-scoped, not permanent -- clearing it on logout is what makes it
  // "ask again at her next sign-in" instead of only on a new browser tab.
  clearAdvisorCodePromptDismissal();
}

/**
 * Removes the cached sale so the next `getSale()` call does a fresh fetch.
 * Almikia Epic E3b (TASK-067): after `salesUpdate` changes the signed-in
 * user's own record (most importantly her advisor_code), the cache would
 * otherwise keep serving the stale value to `getIdentity()` -- including
 * across a page reload -- until she logs out.
 */
export function invalidateCachedSale() {
  getLocalStorage()?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      return baseAuthProvider.checkAuth(params);
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale (role + lead_manager scope)
      const actor = getActorFromSale(sale);
      if (
        !isCarouselResource(params.resource) &&
        !isQuoterPlanResource(params.resource)
      ) {
        return canAccess(actor, params);
      }

      // The carousel gates and the plan-edit scope (TASK-113) need her live project scope
      // (TASK-073 / TASK-074); RLS already limits the rows she can read.
      const { data: memberships } = await getSupabaseClient()
        .from("sales_projects")
        .select("sales_id, project_id, is_lider_for_project, ended_at");
      return canAccess(
        { ...actor, ...getProjectScope(actor.id, memberships ?? []) },
        params,
      );
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
